import React, { useRef, useState, useCallback, useEffect } from 'react'

import { Helmet } from 'react-helmet-async';

import {
    Box,
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import CommonRestricted from '../../common/CommonRestricted';

import { icons } from '../../common/CommonIcons';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useUserRoles, roleTypes, targetTypes } from '../../../hooks/api/useUserRoles';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

export default function ListUserRoles({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@role"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useUserRoles({ roleType: roleTypes.role });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.userRole.id, scopeTypes.userRole.role.id)

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            const result = await list(props)

            return {
                data: result?.data?.roles,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'role_name',
                header: t('name'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`settings/userRole/update/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'role_type', header: t('type')
            },
            {
                name: 'description', header: t('description')
            },
            {
                name: 'status', header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t("enabled"), t("disabled")], ["primary", "default"], "")
            },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.role })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("settings/userRole/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.roles })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("roles")}
                breadcrumbs={[
                    {
                        displayText: t("roles"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
